import Page from 'flarum/common/components/Page';
import app from 'flarum/forum/app';

const GRID_COLS = 5;
const GRID_ROWS = 7;

const CELL_SIZE = 76;
const GRID_GAP = 3;
const CELL_STEP = CELL_SIZE + GRID_GAP;

const TEST_ITEMS = [
  {
    id: 'wood_sword',
    name: '木剑',
    icon: '🗡️',
    image: null,
    width: 1,
    height: 2,
    type: '武器',
    description: '测试武器。以后这里将显示徽章对应的具体游戏效果。'
  },
  {
    id: 'apple',
    name: '苹果',
    icon: '🍎',
    image: null,
    width: 1,
    height: 1,
    type: '食物',
    description: '测试食物。以后可以用于治疗、强化或者触发相邻效果。'
  },
  {
    id: 'wood_shield',
    name: '木盾',
    icon: '🛡️',
    image: null,
    width: 2,
    height: 2,
    type: '防具',
    description: '测试防具。以后可以提供护盾或者防御类效果。'
  }
];

export default class GamePage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.items = TEST_ITEMS.map((item) => ({
      ...item,
      x: null,
      y: null,
      rotated: false
    }));

    this.draggingId = null;
    this.hoverCell = null;

    this.hoveredItemId = null;
    this.selectedItemId = null;
  }

  getItem(id) {
    return this.items.find((item) => item.id === id);
  }

  isPlaced(item) {
    return item.x !== null && item.y !== null;
  }

  getDimensions(item, rotated = item.rotated) {
    return rotated
      ? {
          width: item.height,
          height: item.width
        }
      : {
          width: item.width,
          height: item.height
        };
  }

  canPlace(item, x, y, rotated = item.rotated) {
    const size = this.getDimensions(item, rotated);

    if (
      x < 0 ||
      y < 0 ||
      x + size.width > GRID_COLS ||
      y + size.height > GRID_ROWS
    ) {
      return false;
    }

    for (const other of this.items) {
      if (
        other.id === item.id ||
        !this.isPlaced(other)
      ) {
        continue;
      }

      const otherSize = this.getDimensions(other);

      const overlaps =
        x < other.x + otherSize.width &&
        x + size.width > other.x &&
        y < other.y + otherSize.height &&
        y + size.height > other.y;

      if (overlaps) {
        return false;
      }
    }

    return true;
  }

  placeItem(id, x, y) {
    const item = this.getItem(id);

    if (!item) return false;

    if (!this.canPlace(item, x, y)) {
      return false;
    }

    item.x = x;
    item.y = y;

    return true;
  }

  removeItem(id) {
    const item = this.getItem(id);

    if (!item) return;

    item.x = null;
    item.y = null;
  }

  rotateItem(id) {
    const item = this.getItem(id);

    if (!item) return;

    const nextRotated = !item.rotated;

    if (!this.isPlaced(item)) {
      item.rotated = nextRotated;
      return;
    }

    if (
      this.canPlace(
        item,
        item.x,
        item.y,
        nextRotated
      )
    ) {
      item.rotated = nextRotated;
    }
  }

  handleDragStart(event, item) {
    this.draggingId = item.id;
    this.hoverCell = null;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(
      'text/plain',
      item.id
    );

    /*
     * 不让浏览器使用很大的左侧物品卡片
     * 作为拖拽残影。
     *
     * 真正的占格预览由背包内部负责显示。
     */
    const transparent = document.createElement('canvas');

    transparent.width = 1;
    transparent.height = 1;

    event.dataTransfer.setDragImage(
      transparent,
      0,
      0
    );
  }

  handleDragEnd() {
    this.draggingId = null;
    this.hoverCell = null;
  }

  handleCellDragOver(event, x, y) {
    event.preventDefault();

    this.hoverCell = { x, y };

    const item = this.getItem(this.draggingId);

    if (!item) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }

    event.dataTransfer.dropEffect =
      this.canPlace(item, x, y)
        ? 'move'
        : 'none';
  }

  handleCellDrop(event, x, y) {
    event.preventDefault();

    const id =
      event.dataTransfer.getData('text/plain') ||
      this.draggingId;

    if (!id) return;

    this.placeItem(id, x, y);

    this.draggingId = null;
    this.hoverCell = null;
  }

  renderItemVisual(item) {
    if (item.image) {
      return (
        <img
          className="TMBackpackArena-itemImage"
          src={item.image}
          alt={item.name}
          draggable="false"
        />
      );
    }

    return (
      <div className="TMBackpackArena-itemFallback">
        {item.icon}
      </div>
    );
  }

  renderGridCells() {
    const cells = [];

    for (let y = 0; y < GRID_ROWS; y++) {
      for (let x = 0; x < GRID_COLS; x++) {
        cells.push(
          <div
            className="TMBackpackArena-cell"
            data-x={x}
            data-y={y}
            ondragover={(event) =>
              this.handleCellDragOver(
                event,
                x,
                y
              )
            }
            ondrop={(event) =>
              this.handleCellDrop(
                event,
                x,
                y
              )
            }
          />
        );
      }
    }

    return cells;
  }

  renderDragPreview() {
    if (
      !this.draggingId ||
      !this.hoverCell
    ) {
      return null;
    }

    const item = this.getItem(
      this.draggingId
    );

    if (!item) return null;

    const size = this.getDimensions(item);

    const valid = this.canPlace(
      item,
      this.hoverCell.x,
      this.hoverCell.y
    );

    return (
      <div
        className={
          'TMBackpackArena-dragPreview ' +
          (valid
            ? 'is-valid'
            : 'is-invalid')
        }
        style={{
          left: `${
            this.hoverCell.x * CELL_STEP
          }px`,

          top: `${
            this.hoverCell.y * CELL_STEP
          }px`,

          width: `${
            size.width * CELL_SIZE +
            (size.width - 1) * GRID_GAP
          }px`,

          height: `${
            size.height * CELL_SIZE +
            (size.height - 1) * GRID_GAP
          }px`
        }}
      >
        {this.renderItemVisual(item)}
      </div>
    );
  }

  renderPlacedItem(item) {
    const size = this.getDimensions(item);

    return (
      <div
        className="TMBackpackArena-gridItem"
        draggable="true"
        title={`${item.name} · ${item.type} · ${size.width}×${size.height}`}
        style={{
          left: `${
            item.x * CELL_STEP
          }px`,

          top: `${
            item.y * CELL_STEP
          }px`,

          width: `${
            size.width * CELL_SIZE +
            (size.width - 1) * GRID_GAP
          }px`,

          height: `${
            size.height * CELL_SIZE +
            (size.height - 1) * GRID_GAP
          }px`
        }}

        ondragstart={(event) =>
          this.handleDragStart(
            event,
            item
          )
        }

        ondragend={() =>
          this.handleDragEnd()
        }

        onmouseenter={() => {
          this.hoveredItemId = item.id;
        }}

        onmouseleave={() => {
          this.hoveredItemId = null;
        }}

        onclick={() => {
          this.selectedItemId = item.id;
        }}

        oncontextmenu={(event) => {
          event.preventDefault();
          this.rotateItem(item.id);
        }}
      >
        {this.renderItemVisual(item)}
      </div>
    );
  }

  renderInventoryItem(item) {
    const size = this.getDimensions(item);

    return (
      <div
        className="TMBackpackArena-inventoryItem"
        draggable="true"

        ondragstart={(event) =>
          this.handleDragStart(
            event,
            item
          )
        }

        ondragend={() =>
          this.handleDragEnd()
        }

        onmouseenter={() => {
          this.hoveredItemId = item.id;
        }}

        onmouseleave={() => {
          this.hoveredItemId = null;
        }}

        onclick={() => {
          this.selectedItemId = item.id;
        }}

        oncontextmenu={(event) => {
          event.preventDefault();
          this.rotateItem(item.id);
        }}
      >
        <div className="TMBackpackArena-inventoryIcon">
          {this.renderItemVisual(item)}
        </div>

        <div className="TMBackpackArena-inventoryInfo">
          <strong>
            {item.name}
          </strong>

          <span>
            {item.type} · {size.width}×{size.height}
          </span>

          <small>
            拖入背包使用
          </small>
        </div>
      </div>
    );
  }

  renderInspector() {
    const id =
      this.hoveredItemId ||
      this.selectedItemId;

    const item = id
      ? this.getItem(id)
      : null;

    if (!item) {
      return (
        <div className="TMBackpackArena-inspectorEmpty">
          将鼠标悬停在物品上，或点击物品查看详细信息。
        </div>
      );
    }

    const size = this.getDimensions(item);

    return (
      <div className="TMBackpackArena-inspectorContent">

        <div className="TMBackpackArena-inspectorIcon">
          {this.renderItemVisual(item)}
        </div>

        <div className="TMBackpackArena-inspectorText">

          <strong className="TMBackpackArena-inspectorName">
            {item.name}
          </strong>

          <div>
            类型：{item.type}
          </div>

          <div>
            尺寸：{size.width}×{size.height}
          </div>

          <div className="TMBackpackArena-inspectorDescription">
            {item.description}
          </div>

          <div className="TMBackpackArena-inspectorHint">
            右键物品可以旋转
          </div>

        </div>

      </div>
    );
  }

  view() {
    const user = app.session.user;

    const inventoryItems =
      this.items.filter(
        (item) => !this.isPlaced(item)
      );

    const placedItems =
      this.items.filter(
        (item) => this.isPlaced(item)
      );

    return (
      <div className="TMBackpackArena">
        <div className="TMBackpackArena-window">

          <div className="TMBackpackArena-titlebar">
            背包乱斗 - Tainted Meridian
          </div>

          <div className="TMBackpackArena-content">

            <div className="TMBackpackArena-header">

              <div>
                <h2>背包乱斗</h2>

                {user && (
                  <div className="TMBackpackArena-user">
                    当前玩家：
                    <strong>
                      {user.displayName()}
                    </strong>
                  </div>
                )}
              </div>

              <div className="TMBackpackArena-help">
                拖动物品进入背包 · 右键旋转
              </div>

            </div>

            <div className="TMBackpackArena-gameLayout">

              <section className="TMBackpackArena-panel">

                <div className="TMBackpackArena-panelTitle">
                  测试物品
                </div>

                <div
                  className="TMBackpackArena-inventory"

                  ondragover={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect =
                      'move';
                  }}

                  ondrop={(event) => {
                    event.preventDefault();

                    const id =
                      event.dataTransfer.getData(
                        'text/plain'
                      ) ||
                      this.draggingId;

                    if (id) {
                      this.removeItem(id);
                    }

                    this.draggingId = null;
                    this.hoverCell = null;
                  }}
                >
                  {inventoryItems.length
                    ? inventoryItems.map(
                        (item) =>
                          this.renderInventoryItem(
                            item
                          )
                      )
                    : (
                      <div className="TMBackpackArena-empty">
                        所有测试物品都已放入背包。
                        <br />
                        可以把物品拖回这里。
                      </div>
                    )}
                </div>

              </section>

              <section className="TMBackpackArena-panel">

                <div className="TMBackpackArena-panelTitle">
                  5 × 7 背包
                </div>

                <div className="TMBackpackArena-boardWrap">

                  <div className="TMBackpackArena-board">

                    <div className="TMBackpackArena-grid">
                      {this.renderGridCells()}
                    </div>

                    <div className="TMBackpackArena-itemsLayer">
                      {placedItems.map(
                        (item) =>
                          this.renderPlacedItem(
                            item
                          )
                      )}

                      {this.renderDragPreview()}
                    </div>

                  </div>

                </div>

                <div className="TMBackpackArena-status">
                  已装备：
                  {placedItems.length}
                  {' / '}
                  {this.items.length}
                </div>

                <div className="TMBackpackArena-inspector">

                  <div className="TMBackpackArena-inspectorTitle">
                    物品信息
                  </div>

                  {this.renderInspector()}

                </div>

              </section>

            </div>

          </div>

        </div>
      </div>
    );
  }
}
